import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
// Avoid importing heavy generated DB types to prevent deep type instantiation

import { useSupabase, queryKeys } from '../provider';

// =====================================================
// TRAINEE QUERIES
// =====================================================

interface TraineeFilters {
  locationId?: string;
  status?: string;
  active?: boolean;
  limit?: number;
}

export function useTrainees(filters?: TraineeFilters) {
  const supabase: any = useSupabase();

  return useQuery<any[]>({
    queryKey: queryKeys.trainees.list(filters),
    queryFn: async () => {
      let query = supabase
        .from('trainees')
        .select('*')
        .order('created_at', { ascending: false });

      if (filters?.locationId && filters.locationId.trim() !== '') {
        query = query.eq('location_id', filters.locationId);
      }
      if (filters?.status && filters.status.trim() !== '') {
        query = query.eq('status', filters.status);
      }
      if (filters?.active !== undefined) {
        query = query.eq('active', filters.active);
      }
      if (filters?.limit) {
        query = query.limit(filters.limit);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data;
    },
  });
}

export function useTrainee(traineeId: string | undefined) {
  const supabase: any = useSupabase();

  return useQuery<any>({
    queryKey: queryKeys.trainees.detail(traineeId ?? ''),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('trainees')
        .select(`
          *,
          app_user:app_users(*),
          location:locations(*)
        `)
        .eq('id', traineeId!)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!traineeId && traineeId !== 'new',
  });
}

export function useTraineeCertifications(traineeId: string | undefined) {
  const supabase: any = useSupabase();

  return useQuery<any[]>({
    queryKey: queryKeys.trainees.certifications(traineeId ?? ''),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('trainee_certifications')
        .select(`
          *,
          service_type:service_types(id, service_type_name, service_type_code),
          certified_by_user:app_users(id, full_name)
        `)
        .eq('trainee_id', traineeId!)
        .eq('certification_status', 'Active')
        .order('certification_date', { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!traineeId && traineeId !== 'new',
  });
}

// =====================================================
// TRAINEE MUTATIONS
// =====================================================

type TraineeInsert = any;
type TraineeUpdate = any;
type CertificationInsert = any;

export function useCreateTrainee() {
  const supabase: any = useSupabase();
  const queryClient = useQueryClient();

  return useMutation<any, Error, TraineeInsert>({
    mutationFn: async (trainee: TraineeInsert) => {
      const { data, error } = await supabase
        .from('trainees')
        .insert(trainee as never)
        .select(`*, app_user:app_users(full_name)`)
        .single();

      if (error) throw error;
      return data as any;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trainees.all });
      toast.success('Trainee enrolled successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to enroll trainee: ${error?.message ?? String(error)}`);
    },
  });
}

export function useUpdateTrainee() {
  const supabase: any = useSupabase();
  const queryClient = useQueryClient();

  return useMutation<any, Error, TraineeUpdate & { id: string }>({
    mutationFn: async ({ id, ...updates }: TraineeUpdate & { id: string }) => {
      const { data, error } = await supabase
        .from('trainees')
        .update(updates as never)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as any;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trainees.all });
      toast.success('Trainee updated successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to update trainee: ${error?.message ?? String(error)}`);
    },
  });
}

export function useAddCertification() {
  const supabase: any = useSupabase();
  const queryClient = useQueryClient();

  return useMutation<any, Error, CertificationInsert>({
    mutationFn: async (certification: CertificationInsert) => {
      const { data, error } = await supabase
        .from('trainee_certifications')
        .insert(certification as never)
        .select(`*, service_type:service_types(service_type_name)`)
        .single();

      if (error) throw error;
      return data as any;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trainees.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.trainees.detail(data.trainee_id) });
      queryClient.invalidateQueries({
        queryKey: queryKeys.trainees.certifications(data.trainee_id),
      });
      toast.success('Certification added successfully');
    },
    onError: (error: any) => {
      toast.error(`Failed to add certification: ${error?.message ?? String(error)}`);
    },
  });
}

export function useUpdateCertificationLevel() {
  const supabase: any = useSupabase();
  const queryClient = useQueryClient();

  return useMutation<any, Error, { id: string; level: number; certifiedBy?: string }>({
    mutationFn: async ({
      id,
      level,
      certifiedBy,
    }: {
      id: string;
      level: number;
      certifiedBy?: string;
    }) => {
      const { data, error } = await supabase
        .from('trainee_certifications')
        .update({
          certification_level: level,
          certified_by: certifiedBy,
          certified_date: new Date().toISOString().split('T')[0],
        } as never)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as any;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.trainees.all });
      toast.success(`Certification level updated to ${data.certification_level}`);
    },
    onError: (error: any) => {
      toast.error(`Failed to update certification: ${error?.message ?? String(error)}`);
    },
  });
}
